import {
  type ChatTransport,
  convertToModelMessages,
  createUIMessageStream,
  Output,
  streamText,
  type ToolSet,
  isStepCount,
  toUIMessageStream,
} from 'ai';

import { dixaGateway } from './provider';
import {
  structuredResponseSchema,
  type MimUIMessage,
} from './structuredResponse';

/** stopWhen defaults to 1,
 *  which means that it stops immediately after processing the strucuted output.
 *  Set to 10 so that we have some "budget" to call tools.
 *  See https://ai-sdk.dev/docs/reference/ai-sdk-core/stream-text#stop-when
 */
const STEP_COUNT = 10;

/**
 * Builds a transport that runs the AI SDK agent loop from vercel in the browser
 * using Open SSE streams, facilitated by our own open ai compatible gateway.
 */
export const createBrowserChatTransport = (
  buildTools: () => ToolSet,
  getMainPrompt: () => string,
): ChatTransport<MimUIMessage> => ({
  async sendMessages({ chatId, messages, abortSignal }) {
    const modelMessages = await convertToModelMessages(messages);
    return createUIMessageStream<MimUIMessage>({
      execute: async ({ writer }) => {
        const result = streamText({
          model: dixaGateway(getMainPrompt()),
          messages: modelMessages,
          tools: buildTools(),
          abortSignal,
          headers: { traceparent: `00-${chatId}-4fe7d85c49cf1379-01` },
          output: Output.object({ schema: structuredResponseSchema }),
          stopWhen: isStepCount(STEP_COUNT),
        });

        writer.merge(toUIMessageStream({ stream: result.stream }));

        try {
          const output = await result.output;
          writer.write({
            type: 'data-message',
            id: 'message',
            data: output.message,
          });
          if (output.choices?.length) {
            writer.write({
              type: 'data-choices',
              id: 'choices',
              data: output.choices,
            });
          }
        } catch {
          writer.write({
            type: 'data-message',
            id: 'message',
            data: 'Sorry, I could not produce a response. Please try again.',
          });
        }
      },
    });
  },
  async reconnectToStream() {
    return null;
  },
});
